"use client";

import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";

// ─── Formatting ────────────────────────────────────────────────────────────────

export const MIME_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Word Document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Document",
  "application/vnd.ms-excel": "Excel Spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel Spreadsheet",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
  "text/plain": "Plain Text",
  "text/csv": "CSV",
  "text/markdown": "Markdown",
  "text/html": "HTML",
  "image/png": "PNG Image",
  "image/jpeg": "JPEG Image",
  "image/gif": "GIF Image",
  "image/webp": "WebP Image",
  "application/json": "JSON",
  "text/xml": "XML",
  "application/xml": "XML",
  "text/yaml": "YAML",
  "application/yaml": "YAML",
  "application/zip": "ZIP Archive",
  "application/octet-stream": "4D Method",
};

export const fileTypeLabel = (type?: string | null) =>
  type ? (MIME_LABELS[type] ?? type) : "—";

export const formatBytes = (value?: number) => {
  if (!value || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    units.length - 1
  );
  return `${(value / 1024 ** exponent).toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

// "14 Sep 2026, 2:23 pm" — NZ ordering, lower-cased meridiem.
const nzDateTime = new Intl.DateTimeFormat("en-NZ", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

export const formatNzDate = (value?: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return nzDateTime.format(parsed).replace(/\s?(AM|PM)$/i, (m) => m.toLowerCase());
};

export const formatDetailDate = (value?: string | null) =>
  value ? formatNzDate(value) : "Not available";

export const pluralise = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

// ─── Status ────────────────────────────────────────────────────────────────────

export type KbStatus = "ready" | "processing" | "failed";

export const KB_STATUS_LABELS: Record<KbStatus, string> = {
  ready: "Ready",
  processing: "Processing",
  failed: "Failed",
};

/** Dot colours come from theme tokens only — never a raw palette value. */
export const KB_STATUS_DOT: Record<KbStatus, string> = {
  ready: "bg-activated",
  processing: "bg-brand",
  failed: "bg-destructive",
};

export const KB_STATUS_TEXT: Record<KbStatus, string> = {
  ready: "text-foreground",
  processing: "text-foreground",
  failed: "text-destructive",
};

export const toKbStatus = (status?: string | null): KbStatus => {
  const upper = status?.toUpperCase();
  if (upper === "COMPLETED") return "ready";
  if (upper === "FAILED") return "failed";
  return "processing";
};

/** Worst status wins when summarising a group of copies. */
const STATUS_SEVERITY: Record<KbStatus, number> = { ready: 0, processing: 1, failed: 2 };

export const worstStatus = (statuses: KbStatus[]): KbStatus =>
  statuses.reduce<KbStatus>(
    (worst, next) => (STATUS_SEVERITY[next] > STATUS_SEVERITY[worst] ? next : worst),
    "ready"
  );

export type KbStatusCounts = { all: number; ready: number; processing: number; failed: number };

export const countStatuses = (files: FileManagerTreeNode[]): KbStatusCounts =>
  files.reduce<KbStatusCounts>(
    (counts, file) => {
      counts.all += 1;
      counts[toKbStatus(file.processingStatus)] += 1;
      return counts;
    },
    { all: 0, ready: 0, processing: 0, failed: 0 }
  );

// ─── Team file flattening & duplicate grouping ─────────────────────────────────

/** Every file in a team — direct team files plus everything inside its collections. */
export const flattenTeamFiles = (team: FileManagerTreeNode | null): FileManagerTreeNode[] => {
  if (!team) return [];
  return [
    ...(team.directFiles ?? []),
    ...(team.children ?? []).flatMap((collection) => collection.children ?? []),
  ];
};

export type KbFileRow =
  | { kind: "single"; key: string; file: FileManagerTreeNode }
  | {
      kind: "group";
      key: string;
      name: string;
      files: FileManagerTreeNode[];
      status: KbStatus;
      readyCount: number;
      /** Most recent update across the copies. */
      updatedAt: string | null;
    };

/**
 * Buckets files that share a name into one expandable row. Grouping is skipped
 * while a search or status filter is active, so a filtered view never hides
 * matches behind a collapsed group.
 */
export const groupFileRows = (
  files: FileManagerTreeNode[],
  enabled: boolean
): KbFileRow[] => {
  if (!enabled) {
    return files.map((file) => ({ kind: "single", key: file.id, file }));
  }

  const buckets = new Map<string, FileManagerTreeNode[]>();
  for (const file of files) {
    const key = file.name.trim().toLowerCase();
    const bucket = buckets.get(key);
    if (bucket) bucket.push(file);
    else buckets.set(key, [file]);
  }

  const rows: KbFileRow[] = [];
  for (const [key, bucket] of buckets) {
    if (bucket.length === 1) {
      rows.push({ kind: "single", key: bucket[0].id, file: bucket[0] });
      continue;
    }
    const statuses = bucket.map((file) => toKbStatus(file.processingStatus));
    const latest = bucket.reduce<string | null>((newest, file) => {
      const candidate = file.updatedAt ?? file.createdAt ?? null;
      if (!candidate) return newest;
      if (!newest) return candidate;
      return new Date(candidate) > new Date(newest) ? candidate : newest;
    }, null);
    rows.push({
      kind: "group",
      key: `group:${key}`,
      name: bucket[0].name,
      files: bucket,
      status: worstStatus(statuses),
      readyCount: statuses.filter((status) => status === "ready").length,
      updatedAt: latest,
    });
  }

  return rows;
};

// ─── Drag payload ──────────────────────────────────────────────────────────────

/**
 * What is currently being dragged out of the files table. Held in panel state
 * rather than read back off `dataTransfer`, because the HTML5 drag API only
 * exposes its data on `drop` — never during `dragover`, where the drop targets
 * need it to decide whether they can accept.
 */
export type KbDragPayload = {
  /** Tree node ids (keys into the hook's `nodeIndex`). */
  nodeIds: string[];
  /** Short human description, e.g. `Report.pdf` or `4 copies of Report.pdf`. */
  label: string;
  teamId: string;
  /** False when at least one file is one the signed-in user may not move teams. */
  canMoveTeam: boolean;
};

/** Marker type so external OS file drags are never confused with internal moves. */
export const KB_DRAG_MIME = "application/x-woods-kb-move";
